import {sendAmplitudeEvent} from '#/analytics/amplitude-events';
import {sendAnalyticsEvent} from '#/analytics/analytics';
import {AnalyticsEvent} from '#/analytics/analytics-events';
import {Modal} from '#/atoms/modal';
import {SingleTokenPriceInput} from '#/atoms/single-token-price-input';
import {ModalContext} from '#/contexts/modal-provider';
import {WalletContext} from '#/contexts/wallet-provider';
import {baseKeysUseQuery} from '#/hooks/query/base/base-keys-use-query';
import {userProfileRequests} from '#/repository/fetch-user-profile';
import {ClassName} from '#/utils/utils';
import {BuildingLibraryIcon, CreditCardIcon} from '@heroicons/react/24/solid';
import * as RadioGroup from '@radix-ui/react-radio-group';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {FormEvent, FunctionComponent, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

type TransferMethod = AnalyticsEvent['addFunds']['method'];

type AddFundsModalProps = {
  initialAmount?: number;
};
export const AddFundsModal: FunctionComponent<AddFundsModalProps> = ({initialAmount}) => {
  const {t} = useTranslation('translation');

  const queryClient = useQueryClient();

  const {
    web3AuthSignatureResult: {data: web3AuthIdTokenGroup},
  } = useContext(WalletContext);
  const {open, close} = useContext(ModalContext);

  const [tokenAmountInput, setTokenAmountInput] = useState(initialAmount ? initialAmount.toString() : '');
  const [requestedFundingMethod, setRequestedFundingMethod] = useState<TransferMethod>('bank');
  const requestedFundingAmount = Number(tokenAmountInput);

  const addFundsUserMutationEnabled = !!tokenAmountInput && requestedFundingAmount > 0 && !!web3AuthIdTokenGroup;
  const addFundsUserMutation = useMutation({
    mutationKey: ['add-funds-mutation'],
    mutationFn: async () => {
      return await userProfileRequests.setRequestedFundingAmount(
        {
          requestedFundingAmount,
          requestedFundingMethod,
        },
        web3AuthIdTokenGroup!,
      );
    },
    onSuccess: data => {
      queryClient.setQueryData([baseKeysUseQuery.useUserProfilePrivate, data.account], data);

      close();
      open({
        name: 'CountryNotSupportedModal',
        props: {},
      });
    },
  });

  const isLoading = addFundsUserMutation.isPending;

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!addFundsUserMutationEnabled) return;

    sendAmplitudeEvent.addFunds({
      event_properties: {
        amount: tokenAmountInput,
        method: requestedFundingMethod,
      },
    });

    sendAnalyticsEvent.addFunds({amount: tokenAmountInput, method: requestedFundingMethod});
    await addFundsUserMutation.mutateAsync();
  };

  useEffect(() => {
    sendAnalyticsEvent.openAddFunds({initialAmount: initialAmount?.toString() ?? ''});
    sendAmplitudeEvent.openAddFunds({event_properties: {initialAmount: initialAmount?.toString() ?? ''}});
  }, [initialAmount]);

  return (
    <Modal title={t('pages.addFunds.title')} theme='dark' onClose={close}>
      <form
        className='p-6 pt-4 flex flex-col items-center gap-3 mx-auto w-[340px] relative text-white'
        onSubmit={onSubmit}
      >
        <div className='text-sm font-normal'>{t('pages.addFunds.explanation')}</div>

        <SingleTokenPriceInput
          className='w-full'
          title={'Amount'}
          tokenName={'USDC'}
          step='1'
          tokenAmount={tokenAmountInput}
          onChange={setTokenAmountInput}
        />

        <PaymentSelectionRadioGroup
          transferMethod={requestedFundingMethod}
          setTransferMethod={setRequestedFundingMethod}
        />

        <button
          type='submit'
          className={
            'w-full mt-2 px-4 py-2 text-white rounded flex-center bg-deepblue data-[error=true]:bg-downred data-[loading=true]:bg-gray-400 hover:opacity-70 disabled:opacity-50 transition-all duration-200 ease-in-out'
          }
          data-loading={isLoading}
          data-error={!!addFundsUserMutation.error}
          disabled={!addFundsUserMutationEnabled || isLoading}
        >
          {isLoading
            ? t('statuses.sending')
            : addFundsUserMutation.error
              ? t('actions.errorTryAgain')
              : t('actions.next')}
        </button>

        <div className='flex gap-1 text-sm text-gray-400 items-start'>
          <div className='aspect-square flex-center p-1 group'>
            <span className={'w-3 h-3 rounded-full bg-gray-400 text-grayblue text-xs flex-center select-none'}>
              {t('symbols.info')}
            </span>
          </div>
          <span>{t('pages.addFunds.disclaimer')}</span>
        </div>
      </form>
    </Modal>
  );
};

type PaymentSelectionRadioGroupProps = {
  transferMethod: TransferMethod;
  setTransferMethod: (method: TransferMethod) => void;
};
const PaymentSelectionRadioGroup: FunctionComponent<PaymentSelectionRadioGroupProps> = ({
  transferMethod,
  setTransferMethod,
}) => {
  const {t} = useTranslation('translation');

  const items = useMemo(
    () =>
      [
        {value: 'bank', label: t('pages.addFunds.methods.bank.title'), IconComponent: BuildingLibraryIcon},
        {
          value: 'credit_card',
          label: t('pages.addFunds.methods.creditCard.title'),
          IconComponent: CreditCardIcon,
        },
      ] satisfies PaymentSelectionRadioItemProps[],
    [t],
  );

  const onValueChange = useCallback((val: string) => setTransferMethod(val as TransferMethod), [setTransferMethod]);

  return (
    <RadioGroup.Root
      className='w-full py-1 grid grid-cols-2 grid-rows-1 gap-3'
      value={transferMethod}
      onValueChange={onValueChange}
      {...({} as any)}
    >
      {items.map((props, index) => (
        <PaymentSelectionRadioItem key={index} {...props} />
      ))}
    </RadioGroup.Root>
  );
};

type PaymentSelectionRadioItemProps = {
  value: TransferMethod;
  label: string;
  IconComponent: FunctionComponent<ClassName>;
};
const PaymentSelectionRadioItem: FunctionComponent<PaymentSelectionRadioItemProps> = ({
  value,
  label,
  IconComponent,
  ...rest
}) => {
  return (
    <RadioGroup.Item
      {...rest}
      className='relative py-3 bg-graybluedark2 border-2 border-transparent data-[state=checked]:border-gray-200 text-base font-bold text-gray-200 rounded flex-center gap-2 hover:opacity-70 disabled:grayscale disabled:opacity-50 disabled:pointer-events-none'
      value={value}
    >
      <IconComponent className='w-4 aspect-square fill-gray-200 stroke-gray-200' />
      {label}
    </RadioGroup.Item>
  );
};
